import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  CircularProgress,
  Tabs,
  Tab,
  Card,
  CardContent,
} from "@mui/material";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";

const ROWS_PER_PAGE = 10;

const medal = (rank) => {
  if (rank === 1) return "🥇";
  if (rank === 2) return "🥈";
  if (rank === 3) return "🥉";
  return rank;
};

const Leaderboard = () => {
  const [allTime, setAllTime] = useState([]);
  const [monthly, setMonthly] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [tab, setTab] = useState(0);
  const [page, setPage] = useState(0);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        // /users is the full customer directory — names, emails, phone
        // numbers — and is staff-only. This page only ever drew a display name
        // and two scores, which is exactly what /public/leaderboard returns.
        const res = await fetch(
          "https://damp-thicket-07899-72417fa8b047.herokuapp.com/public/leaderboard"
        );
        const data = await res.json();

        const all = [...data]
          .sort((a, b) => b.totalPoints - a.totalPoints)
          .map((u, i) => ({ ...u, rank: i + 1 }));

        const month = [...data]
          .filter((u) => u.monthlyPoints > 0)
          .sort((a, b) => b.monthlyPoints - a.monthlyPoints)
          .map((u, i) => ({ ...u, rank: i + 1 }));

        setAllTime(all);
        setMonthly(month);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, []);

  const rows = tab === 0 ? allTime : monthly;

  const filtered = useMemo(
    () =>
      rows.filter((r) =>
        r.username?.toLowerCase().includes(search.toLowerCase())
      ),
    [rows, search]
  );

  if (loading) {
    return (
      <Box sx={{ py: 10, textAlign: "center" }}>
        <CircularProgress />
        <Typography sx={{ mt: 2, color: "text.secondary" }}>
          Loading leaderboard…
        </Typography>
      </Box>
    );
  }

  return (
    <>
    <Helmet>
      <title>Leaderboard | Den Den Claw Ottawa</title>
      <meta
        name="description"
        content="See the top players at Den Den Claw, Ottawa's Japanese-style claw machine arcade. Check the all-time and monthly leaderboards."
      />
      <link rel="canonical" href="https://www.dendenclaw.com/explore/leaderboard" />
    </Helmet>
    <Box sx={{ py: 6 }}>
      <Typography
        variant="h4"
        sx={{
          textAlign: "center",
          fontWeight: 800,
          color: "primary.main",
          mb: 1,
        }}
      >
        Leaderboard 🏆
      </Typography>

      <Typography
        sx={{
          textAlign: "center",
          color: "text.secondary",
          mb: 4,
        }}
      >
        See who’s been crushing it at Den Den Claw
      </Typography>

      <Card
        sx={{
          maxWidth: 900,
          mx: "auto",
          borderRadius: 3,
          boxShadow: 6,
          background: "linear-gradient(180deg, #f5faff 0%, #ffffff 100%)",
        }}
      >
        <CardContent>
          {/* TABS */}
          <Tabs
            value={tab}
            onChange={(_, v) => {
              setTab(v);
              setPage(0);
            }}
            centered
            sx={{ mb: 3 }}
          >
            <Tab label="All Time" />
            <Tab label="Event" />
          </Tabs>

          {/* SEARCH */}
          <TextField
            fullWidth
            label="Search username"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(0);
            }}
            sx={{ mb: 3 }}
          />

          {/* TABLE */}
          <Table sx={{ borderRadius: 2, overflow: "hidden" }}>
            <TableHead sx={{ backgroundColor: "#0f7ca4" }}>
              <TableRow>
                <TableCell sx={{ color: "#fff", fontWeight: 600 }}>
                  Rank
                </TableCell>
                <TableCell sx={{ color: "#fff", fontWeight: 600 }}>
                  Player
                </TableCell>
                <TableCell sx={{ color: "#fff", fontWeight: 600 }}>
                  Points
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {filtered
                .slice(
                  page * ROWS_PER_PAGE,
                  page * ROWS_PER_PAGE + ROWS_PER_PAGE
                )
                .map((row) => (
               <TableRow
                  key={row._id}
                  sx={{
                    background:
                      row.rank === 1
                        ? "linear-gradient(90deg, #fff4cc 0%, #ffecb3 100%)"
                        : row.rank <= 3
                        ? "#e8f2ff"
                        : "#fff",
                    fontWeight: row.rank === 1 ? 700 : 400,
                    boxShadow: row.rank === 1 ? "inset 0 0 0 1px #f5c16c" : "none",
                    "&:hover": {
                      background:
                        row.rank === 1
                          ? "linear-gradient(90deg, #ffefb0 0%, #ffe082 100%)"
                          : "#dff1ff",
                    },
                  }}
                >

                    <TableCell>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        {row.rank === 1 && (
                          <EmojiEventsIcon color="warning" />
                        )}
                        {medal(row.rank)}
                      </Box>
                    </TableCell>
                    <TableCell>{row.username}</TableCell>
                    <TableCell>
                      {tab === 0 ? row.totalPoints : row.monthlyPoints}
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>

          <TablePagination
            component="div"
            count={filtered.length}
            page={page}
            onPageChange={(_, p) => setPage(p)}
            rowsPerPage={ROWS_PER_PAGE}
            rowsPerPageOptions={[ROWS_PER_PAGE]}
          />
        </CardContent>
      </Card>
    </Box>
    </>
  );
};

export default Leaderboard;
